import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api, { postWhatsAppSend } from '../services/api'
import { config, resolveWhatsAppMediaUrl } from '../config/app'
import Swal from 'sweetalert2'
import { normalizeBrazilPhoneForStorage } from '../utils/phoneBr'
import {
  formatWhatsAppMessageHtml,
  normalizeMessageLineBreaks,
  prepareWhatsAppOutboundText
} from '../utils/whatsappMessageFormat'
import { parseYoutubeLinksFromText, textWithoutYoutubeUrls } from '../utils/youtubeLink'
import {
  startWhatsAppVoiceRecording,
  stopWhatsAppVoiceRecording,
  cancelWhatsAppVoiceRecording
} from '../utils/whatsappVoiceRecorder'

/**
 * Chat WhatsApp compartilhado (lead ou cliente).
 */
export function useWhatsAppChat(options) {
  const { t, locale } = useI18n()

  const entityType = computed(() => options.entityType?.value ?? options.entityType ?? 'lead')
  const entityId = computed(() => options.entityId?.value ?? options.entityId ?? null)
  const workspaceId = computed(() => options.workspaceId?.value ?? options.workspaceId ?? null)
  const contactPhone = computed(() => options.contactPhone?.value ?? options.contactPhone ?? '')
  const contactLid = computed(() => options.contactLid?.value ?? options.contactLid ?? '')
  const active = computed(() => options.active?.value ?? options.active ?? false)
  const showChatbot = computed(() => options.showChatbot?.value ?? options.showChatbot ?? false)

  const dateLocale = computed(() => {
    if (locale.value === 'en') return 'en-US'
    if (locale.value === 'es') return 'es'
    return 'pt-BR'
  })

  const chatMessages = ref([])
  const newMessage = ref('')
  const showEmojiPicker = ref(false)
  const messageInputEl = ref(null)
  const fileDoc = ref(null)
  const fileImg = ref(null)
  const autoScrollEnabled = ref(true)
  const chatContainer = ref(null)
  const previewWaImage = ref(null)

  const whatsAppStatus = ref('')
  const whatsAppSocketOpen = ref(false)
  const whatsAppCanQueue = ref(false)
  const whatsAppSocketState = ref('')
  const whatsAppConnected = computed(() => String(whatsAppStatus.value || '').toUpperCase() === 'CONNECTED')
  const whatsAppCanSend = computed(() => whatsAppConnected.value && whatsAppSocketOpen.value)
  const whatsAppOffline = computed(() => !whatsAppConnected.value)
  let healthPollTimer = null
  const HEALTH_POLL_MS = 3000

  const chatbotSession = ref({ flowAtivo: false, status: null, canEndAttendance: false })
  const chatbotEnding = ref(false)

  const isRecording = ref(false)
  const showChatComposer = ref(true)
  const COMPOSER_REVEAL_DELAY_MS = 500
  const recordingDuration = ref(0)
  let composerRevealTimer = null
  let recordingTimer = null
  let recordingStartTime = 0
  let finishingRecording = false
  const MIN_RECORDING_DURATION = 400
  const MIN_BLOB_BYTES = 32

  const messagesBasePath = computed(() => {
    const id = entityId.value
    if (!id) return null
    return entityType.value === 'cliente'
      ? `/whatsapp/cliente/${id}`
      : `/whatsapp/lead/${id}`
  })

  const resolveRecipientPhone = () =>
    normalizeBrazilPhoneForStorage(contactPhone.value || '')

  const resolveRecipient = () => {
    const phone = resolveRecipientPhone()
    if (phone) return phone
    const lid = String(contactLid.value || '').trim()
    return lid || ''
  }

  const refreshWhatsAppStatus = async () => {
    const wid = workspaceId.value
    if (!wid) {
      whatsAppStatus.value = ''
      whatsAppSocketOpen.value = false
      whatsAppCanQueue.value = false
      whatsAppSocketState.value = ''
      return { connected: false, canSend: false, canQueue: false }
    }
    whatsAppSocketOpen.value = false
    whatsAppCanQueue.value = false
    try {
      const r = await api.get(`/whatsapp/workspace/${wid}`)
      whatsAppStatus.value = r.data?.status || ''
      if (whatsAppConnected.value) {
        const health = await api.get(`/whatsapp/workspace/${wid}/gateway-health`)
        whatsAppSocketOpen.value = health.data?.canSend === true
        whatsAppCanQueue.value = health.data?.canQueue === true
        whatsAppSocketState.value = health.data?.socketState || ''
      }
    } catch {
      whatsAppStatus.value = ''
      whatsAppSocketOpen.value = false
      whatsAppCanQueue.value = false
      whatsAppSocketState.value = ''
    }
    return {
      connected: whatsAppConnected.value,
      canSend: whatsAppCanSend.value,
      canQueue: whatsAppCanQueue.value
    }
  }

  const startHealthPoll = () => {
    stopHealthPoll()
    if (!active.value || !whatsAppConnected.value) return
    healthPollTimer = setInterval(() => {
      refreshWhatsAppStatus()
    }, HEALTH_POLL_MS)
  }

  const stopHealthPoll = () => {
    if (healthPollTimer) {
      clearInterval(healthPollTimer)
      healthPollTimer = null
    }
  }

  const loadChatbotSession = async () => {
    if (!showChatbot.value) {
      chatbotSession.value = { flowAtivo: false, status: null, canEndAttendance: false }
      return
    }
    const wid = workspaceId.value
    const id = entityId.value
    if (!wid || !id || !whatsAppConnected.value) {
      chatbotSession.value = { flowAtivo: false, status: null, canEndAttendance: false }
      return
    }
    try {
      const { data } = await api.get(`/whatsapp/workspace/${wid}/chatbot/leads/${id}/session`)
      chatbotSession.value = {
        flowAtivo: !!data.flowAtivo,
        status: data.status ?? null,
        canEndAttendance: !!data.canEndAttendance
      }
    } catch {
      chatbotSession.value = { flowAtivo: false, status: null, canEndAttendance: false }
    }
  }

  const endChatbotAttendance = async () => {
    const wid = workspaceId.value
    const id = entityId.value
    if (!showChatbot.value || !wid || !id || chatbotEnding.value) return
    chatbotEnding.value = true
    try {
      const { data } = await api.post(`/whatsapp/workspace/${wid}/chatbot/leads/${id}/end-attendance`)
      chatbotSession.value = {
        flowAtivo: !!data.flowAtivo,
        status: data.status ?? null,
        canEndAttendance: !!data.canEndAttendance
      }
      await loadChatHistory()
      await Swal.fire({
        title: t('common.success'),
        text: t('kanban.leadDetail.whatsapp.endAttendanceSuccess'),
        icon: 'success',
        timer: 2200,
        showConfirmButton: false
      })
    } catch (e) {
      const errMsg = e.response?.data?.error || e.message || t('kanban.leadDetail.whatsapp.endAttendanceError')
      await Swal.fire({ title: t('integracao.swal.error'), text: errMsg, icon: 'error' })
    } finally {
      chatbotEnding.value = false
    }
  }

  const filenameFromMediaUrl = (url) => {
    if (!url) return null
    const raw = String(url)
    const patterns = [
      /\/backlog-files\/whatsapp\/([^/?#]+)/i,
      /\/whatsapp\/([^/?#]+\.[a-z0-9]{2,10})/i,
      /\/([^/?#]+\.[a-z0-9]{2,10})$/i
    ]
    for (const re of patterns) {
      const m = raw.match(re)
      if (m?.[1]) return m[1]
    }
    return null
  }

  const mediaBasenameForDedupe = (url) => filenameFromMediaUrl(url) || String(url || '').split('/').pop()?.split('?')[0] || ''

  const resolveTimestamp = (m) => {
    try {
      if (m?.timestampMessage > 0) return new Date(m.timestampMessage)
      if (m?.dataCriacao) {
        const raw = String(m.dataCriacao)
        const iso = raw.includes('T') ? raw.split('.')[0] : raw
        const dt = new Date(iso)
        if (!isNaN(dt.getTime())) return dt
      }
    } catch { /* ignore */ }
    return new Date()
  }

  const mapApiMessageToChat = (m) => {
    if (!m?.id) return null
    const type = (m.messageType || 'TEXT').toUpperCase()
    const isMedia = ['IMAGE', 'AUDIO', 'DOCUMENT', 'VIDEO'].includes(type)
    const rawMedia = isMedia ? m.message : null
    return {
      id: m.id,
      text: isMedia ? normalizeMessageLineBreaks(m.caption || '') : normalizeMessageLineBreaks(m.message || ''),
      type,
      mediaUrl: isMedia ? resolveWhatsAppMediaUrl(rawMedia) : null,
      fileName: m.fileName || filenameFromMediaUrl(rawMedia) || (type === 'DOCUMENT' ? 'Documento' : null),
      caption: m.caption || null,
      sent: String(m.direction || 'OUTGOING').toUpperCase() === 'OUTGOING',
      status: String(m.status || 'SENT').toUpperCase(),
      timestamp: resolveTimestamp(m),
      audioLoadFailed: false,
      durationSeconds: m.durationSeconds ?? m.seconds ?? null
    }
  }

  const upsertChatMessage = (raw) => {
    const mapped = mapApiMessageToChat(raw)
    if (!mapped) return
    const idx = chatMessages.value.findIndex((x) => Number(x.id) === Number(mapped.id))
    if (idx >= 0) {
      chatMessages.value[idx] = mapped
      return
    }
    if (mapped.sent && mapped.mediaUrl) {
      const base = mediaBasenameForDedupe(mapped.mediaUrl)
      const ts = mapped.timestamp ? new Date(mapped.timestamp).getTime() : Date.now()
      const dupIdx = chatMessages.value.findIndex((x) => {
        if (!x.sent || !x.mediaUrl) return false
        const xTs = x.timestamp ? new Date(x.timestamp).getTime() : 0
        return mediaBasenameForDedupe(x.mediaUrl) === base && Math.abs(ts - xTs) < 30000
      })
      if (dupIdx >= 0) return
    }
    chatMessages.value.push(mapped)
  }

  const matchesEntity = (msg) => {
    if (!msg || !entityId.value) return false
    if (entityType.value === 'cliente') {
      return Number(msg.clienteId) === Number(entityId.value)
    }
    return Number(msg.leadId) === Number(entityId.value)
  }

  const markAllRead = async () => {
    const base = messagesBasePath.value
    if (!base) return
    try {
      await api.post(`${base}/mark-all-read`)
      if (entityType.value === 'lead') {
        window.dispatchEvent(new CustomEvent('lead.messages.read', { detail: { leadId: entityId.value } }))
      }
    } catch (e) {
      console.warn('[useWhatsAppChat] mark-all-read:', e)
    }
  }

  const loadChatHistory = async () => {
    const base = messagesBasePath.value
    if (!base || !whatsAppConnected.value) {
      chatMessages.value = []
      return
    }
    try {
      const response = await api.get(`${base}/messages`, { params: { _t: Date.now() } })
      const list = Array.isArray(response.data) ? response.data : []
      chatMessages.value = list.map(mapApiMessageToChat).filter(Boolean)
      await markAllRead()
    } catch (e) {
      console.error('[useWhatsAppChat] loadChatHistory:', e)
    }
  }

  const buildSendBody = (body) => {
    const recipient = resolveRecipient()
    const payload = { recipient, ...body }
    if (entityType.value === 'cliente') {
      payload.clienteId = entityId.value
    } else {
      payload.leadId = entityId.value
    }
    return payload
  }

  const scrollToBottom = () => {
    if (!chatContainer.value) return
    nextTick(() => {
      chatContainer.value.scrollTop = chatContainer.value.scrollHeight
    })
  }

  const toggleAutoScroll = () => {
    autoScrollEnabled.value = !autoScrollEnabled.value
    if (autoScrollEnabled.value) scrollToBottom()
  }

  watch(chatMessages, () => {
    if (autoScrollEnabled.value) scrollToBottom()
  }, { deep: true })

  const autoResizeWhatsAppMessage = (eventOrEl) => {
    const el = eventOrEl?.target ?? eventOrEl ?? messageInputEl.value
    if (!el) return
    el.style.height = 'auto'
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 22
    const maxHeight = lineHeight * 6 + 16
    const newHeight = Math.min(el.scrollHeight, maxHeight)
    el.style.height = `${newHeight}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }

  const insertEmoji = (emoji) => {
    const input = messageInputEl.value
    const current = newMessage.value || ''
    if (input && typeof input.selectionStart === 'number') {
      const start = input.selectionStart
      const end = input.selectionEnd ?? start
      newMessage.value = current.slice(0, start) + emoji + current.slice(end)
      nextTick(() => {
        const pos = start + emoji.length
        input.focus()
        input.setSelectionRange(pos, pos)
        autoResizeWhatsAppMessage(input)
      })
    } else {
      newMessage.value = current + emoji
      nextTick(() => {
        messageInputEl.value?.focus()
        autoResizeWhatsAppMessage(messageInputEl.value)
      })
    }
  }

  const toggleEmojiPicker = () => {
    showEmojiPicker.value = !showEmojiPicker.value
  }

  const showWhatsAppUnavailable = async () => {
    const text = !whatsAppConnected.value
      ? t('kanban.leadDetail.whatsapp.connectBeforeSend')
      : (!whatsAppCanQueue.value
        ? t('kanban.leadDetail.whatsapp.gatewayUnreachable')
        : t('kanban.leadDetail.whatsapp.gatewayReconnecting'))
    await Swal.fire({
      title: t('kanban.leadDetail.whatsapp.unavailableTitle'),
      text,
      icon: 'warning'
    })
  }

  const ensureWhatsAppCanSend = async () => {
    if (whatsAppCanQueue.value) return true
    await refreshWhatsAppStatus()
    if (whatsAppCanQueue.value) return true
    await showWhatsAppUnavailable()
    return false
  }

  const composerStatusKey = computed(() => {
    if (whatsAppOffline.value) return 'kanban.leadDetail.whatsapp.disconnected'
    if (whatsAppCanSend.value) return 'kanban.leadDetail.whatsapp.readyToSend'
    if (whatsAppCanQueue.value) return 'kanban.leadDetail.whatsapp.queuedToSend'
    return 'kanban.leadDetail.whatsapp.gatewayReconnecting'
  })

  const sendTextMessage = async (textOverride) => {
    const raw = textOverride != null ? String(textOverride) : newMessage.value
    const text = prepareWhatsAppOutboundText(raw).replace(/^\n+|\n+$/g, '')
    if (!text || !entityId.value) return false

    if (!(await ensureWhatsAppCanSend())) return false

    const recipient = resolveRecipient()
    if (!recipient) {
      const msgKey = entityType.value === 'cliente'
        ? 'workspaceClienteModal.whatsapp.phoneRequired'
        : 'kanban.leadDetail.whatsapp.phoneRequired'
      await Swal.fire({
        title: t('integracao.swal.error'),
        text: t(msgKey),
        icon: 'warning'
      })
      return false
    }

    try {
      const res = await postWhatsAppSend(
        `/whatsapp/workspace/${workspaceId.value}/send`,
        buildSendBody({ message: text, messageType: 'TEXT' })
      )
      upsertChatMessage(res.data)
      if (textOverride == null) {
        newMessage.value = ''
        nextTick(() => autoResizeWhatsAppMessage())
      }
      scrollToBottom()
      await loadChatbotSession()
      return true
    } catch (e) {
      const errMsg = e.response?.data?.error || e.message || 'Não foi possível enviar a mensagem.'
      await Swal.fire({ title: t('kanban.leadDetail.swal.sendErrorTitle'), text: errMsg, icon: 'error' })
      return false
    }
  }

  const sendMessage = async () => {
    await sendTextMessage()
  }

  const sendDocumentFile = async (file, caption = '') => {
    if (!file || !entityId.value) return false
    if (!(await ensureWhatsAppCanSend())) return false

    const recipient = resolveRecipient()
    if (!recipient) {
      await Swal.fire({
        title: t('integracao.swal.error'),
        text: t('kanban.leadDetail.whatsapp.phoneRequired'),
        icon: 'warning'
      })
      return false
    }

    try {
      const base64 = await readAsDataUrl(file)
      const uploadRes = await fetch(config.whatsGatewayUrl + '/gateway/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          base64,
          mimetype: file.type || 'application/pdf'
        })
      })
      if (!uploadRes.ok) {
        const errBody = await uploadRes.json().catch(() => ({}))
        throw new Error(errBody.error || `Falha no upload (${uploadRes.status})`)
      }
      const upload = await uploadRes.json()
      if (!upload?.url) throw new Error('Falha no upload')

      const res = await postWhatsAppSend(
        `/whatsapp/workspace/${workspaceId.value}/send`,
        buildSendBody({
          message: caption || undefined,
          messageType: 'DOCUMENT',
          media: {
            type: 'document',
            url: upload.url,
            fileName: file.name,
            mimetype: file.type || 'application/pdf',
            caption: caption || undefined
          }
        })
      )
      upsertChatMessage(res.data)
      scrollToBottom()
      await loadChatbotSession()
      return true
    } catch (e) {
      await Swal.fire({
        title: 'Erro ao enviar arquivo',
        text: e.response?.data?.error || e.message || 'Tente novamente.',
        icon: 'error'
      })
      return false
    }
  }

  const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

  const handleFilePick = async (kind, evt) => {
    try {
      const input = evt?.target
      const file = input?.files?.[0]
      if (!file || !entityId.value) return
      if (!(await ensureWhatsAppCanSend())) return
      const recipient = resolveRecipient()
      if (!recipient) {
        await Swal.fire({ title: t('integracao.swal.error'), text: t('kanban.leadDetail.whatsapp.phoneRequired'), icon: 'warning' })
        return
      }
      const base64 = await readAsDataUrl(file)
      const uploadRes = await fetch(config.whatsGatewayUrl + '/gateway/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, base64, mimetype: file.type })
      })
      if (!uploadRes.ok) {
        const errBody = await uploadRes.json().catch(() => ({}))
        throw new Error(errBody.error || `Falha no upload (${uploadRes.status})`)
      }
      const upload = await uploadRes.json()
      if (!upload?.url) throw new Error('Falha no upload')
      const mediaType = kind === 'image' ? 'image' : 'document'
      const res = await postWhatsAppSend(
        `/whatsapp/workspace/${workspaceId.value}/send`,
        buildSendBody({
          messageType: mediaType.toUpperCase(),
          media: { type: mediaType, url: upload.url, fileName: file.name, mimetype: file.type }
        })
      )
      upsertChatMessage(res.data)
      scrollToBottom()
      if (input) input.value = ''
    } catch (e) {
      await Swal.fire({
        title: 'Erro ao enviar arquivo',
        text: e.response?.data?.error || e.message || 'Tente novamente.',
        icon: 'error'
      })
    }
  }

  const triggerFilePick = (kind) => {
    if (kind === 'document') fileDoc.value?.click()
    else if (kind === 'image') fileImg.value?.click()
  }

  const clearComposerRevealTimer = () => {
    if (composerRevealTimer) {
      clearTimeout(composerRevealTimer)
      composerRevealTimer = null
    }
  }

  const hideChatComposer = () => {
    clearComposerRevealTimer()
    showChatComposer.value = false
    showEmojiPicker.value = false
  }

  const scheduleShowChatComposer = () => {
    clearComposerRevealTimer()
    composerRevealTimer = setTimeout(() => {
      showChatComposer.value = true
      composerRevealTimer = null
    }, COMPOSER_REVEAL_DELAY_MS)
  }

  const cancelRecording = ({ force = false } = {}) => {
    if (!isRecording.value && !force) return
    finishingRecording = false
    isRecording.value = false
    clearInterval(recordingTimer)
    scheduleShowChatComposer()
    recordingStartTime = 0
    cancelWhatsAppVoiceRecording()
  }

  const processRecording = async (audioBlob, mimeType, durationSeconds) => {
    if (!entityId.value) return
    if (!audioBlob || audioBlob.size < MIN_BLOB_BYTES) {
      console.warn('[useWhatsAppChat] Gravação vazia:', {
        size: audioBlob?.size ?? 0,
        mimeType
      })
      await Swal.fire({
        title: t('integracao.swal.error'),
        text: t('kanban.leadDetail.whatsapp.audioNotRecorded'),
        icon: 'warning'
      })
      return
    }

    try {
      if (!(await ensureWhatsAppCanSend())) return

      const base64Audio = await readAsDataUrl(audioBlob)
      let ext = 'ogg'
      if (mimeType.includes('webm')) ext = 'webm'
      else if (mimeType.includes('mp4')) ext = 'm4a'
      const fileName = `audio_${Date.now()}.${ext}`
      const recipient = resolveRecipient()
      if (!recipient) {
        await Swal.fire({
          title: t('integracao.swal.error'),
          text: t('kanban.leadDetail.whatsapp.phoneRequiredForAudio'),
          icon: 'warning'
        })
        return
      }

      const uploadRes = await fetch(config.whatsGatewayUrl + '/gateway/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName, base64: base64Audio, mimetype: mimeType })
      })
      if (!uploadRes.ok) {
        const errBody = await uploadRes.json().catch(() => ({}))
        throw new Error(errBody.error || `Falha no upload do áudio (${uploadRes.status})`)
      }
      const upload = await uploadRes.json()
      if (!upload?.url) throw new Error('Falha no upload do áudio')

      const seconds = durationSeconds != null ? Math.max(1, Math.ceil(durationSeconds)) : null
      const res = await postWhatsAppSend(
        `/whatsapp/workspace/${workspaceId.value}/send`,
        buildSendBody({
          messageType: 'AUDIO',
          media: {
            type: 'audio',
            url: upload.url,
            fileName,
            mimetype: mimeType,
            ptt: true,
            ...(seconds != null ? { seconds } : {})
          }
        })
      )
      const raw = res.data
      if (raw && seconds != null) raw.durationSeconds = seconds
      upsertChatMessage(raw)
      scrollToBottom()
    } catch (e) {
      console.error('[useWhatsAppChat] processRecording:', e)
      await Swal.fire({
        title: t('integracao.swal.error'),
        text: e?.message || t('kanban.leadDetail.whatsapp.audioSendError'),
        icon: 'error'
      })
    }
  }

  const stopRecording = async () => {
    if (!isRecording.value || finishingRecording) return
    const duration = Date.now() - recordingStartTime
    if (duration < MIN_RECORDING_DURATION) {
      cancelRecording({ force: true })
      await Swal.fire({
        title: t('integracao.swal.error'),
        text: t('kanban.leadDetail.whatsapp.audioTooBrief'),
        icon: 'warning'
      })
      return
    }

    finishingRecording = true
    isRecording.value = false
    clearInterval(recordingTimer)
    scheduleShowChatComposer()

    try {
      const { blob, mimeType, durationSeconds } = await stopWhatsAppVoiceRecording()
      if (!blob || blob.size < MIN_BLOB_BYTES) {
        console.warn('[useWhatsAppChat] Gravação vazia após stop:', { duration, mimeType, size: blob?.size })
      }
      await processRecording(blob, mimeType, durationSeconds)
    } catch (e) {
      console.error('[useWhatsAppChat] stopRecording:', e)
      cancelRecording({ force: true })
      await Swal.fire({
        title: t('integracao.swal.error'),
        text: t('kanban.leadDetail.whatsapp.audioNotRecorded'),
        icon: 'error'
      })
    } finally {
      finishingRecording = false
    }
  }

  const startRecording = async () => {
    if (isRecording.value || finishingRecording) return
    try {
      await startWhatsAppVoiceRecording()
      recordingStartTime = Date.now()
      isRecording.value = true
      hideChatComposer()
      recordingDuration.value = 0
      recordingTimer = setInterval(() => {
        recordingDuration.value++
        if (recordingDuration.value >= 60) stopRecording()
      }, 1000)
    } catch {
      cancelWhatsAppVoiceRecording()
      await Swal.fire({
        title: t('integracao.swal.error'),
        text: t('kanban.leadDetail.whatsapp.micAccessError'),
        icon: 'error'
      })
      isRecording.value = false
      showChatComposer.value = true
    }
  }

  const toggleRecording = async () => {
    if (isRecording.value) await stopRecording()
    else await startRecording()
  }

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString(dateLocale.value, { hour: '2-digit', minute: '2-digit' })

  const formatVoiceDuration = (seconds) => {
    const total = Math.max(0, Number(seconds) || 0)
    const mins = Math.floor(total / 60)
    const secs = total % 60
    return `${mins}:${String(secs).padStart(2, '0')}`
  }

  const renderWhatsAppMessage = (text, sent) =>
    formatWhatsAppMessageHtml(text, {
      linkClass: sent ? 'whatsapp-message-link whatsapp-message-link--sent' : 'whatsapp-message-link'
    })

  const getYoutubePreviews = (text) => parseYoutubeLinksFromText(text)
  const messageTextWithoutYoutube = (text) => textWithoutYoutubeUrls(text, getYoutubePreviews(text))
  const hasYoutubePreview = (message) => {
    if (message?.type && message.type !== 'TEXT') return false
    return getYoutubePreviews(message?.text).length > 0
  }
  const isMediaBubble = (message) => {
    const type = String(message?.type || 'TEXT').toUpperCase()
    return ['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT'].includes(type)
  }

  const GENERATED_FILE_RE = /^\d{10,}_[a-z0-9]+\.[a-z0-9]+$/i
  const fileExtension = (message) => {
    const name = message?.fileName || filenameFromMediaUrl(message?.mediaUrl) || ''
    const match = String(name).match(/\.([a-z0-9]+)$/i)
    return match ? match[1].toLowerCase() : 'arq'
  }
  const displayFileName = (message) => {
    const name = message?.fileName || filenameFromMediaUrl(message?.mediaUrl) || 'Documento'
    if (GENERATED_FILE_RE.test(String(name))) {
      const ext = fileExtension(message)
      return ext && ext !== 'arq' ? `Documento.${ext}` : 'Documento'
    }
    return name
  }
  const fileTypeLabel = (ext) => {
    const labels = { pdf: 'PDF', xlsx: 'Planilha Excel', xls: 'Planilha Excel', doc: 'Documento Word', docx: 'Documento Word' }
    return labels[ext] || (ext && ext !== 'arq' ? ext.toUpperCase() : 'Arquivo')
  }
  const fileIconClass = (message) => {
    const ext = fileExtension(message)
    if (ext === 'pdf') return 'wa-doc-icon--pdf'
    if (['xlsx', 'xls', 'csv'].includes(ext)) return 'wa-doc-icon--sheet'
    if (['doc', 'docx'].includes(ext)) return 'wa-doc-icon--doc'
    if (['zip', 'rar', '7z'].includes(ext)) return 'wa-doc-icon--archive'
    return 'wa-doc-icon--default'
  }

  const openWaImagePreview = (message) => {
    if (!message?.mediaUrl) return
    previewWaImage.value = { url: message.mediaUrl, caption: message.caption || '' }
  }
  const closeWaImagePreview = () => {
    previewWaImage.value = null
  }

  const parseWsWhatsAppPayload = (detail) => {
    if (detail == null) return null
    if (typeof detail === 'string') {
      try { return JSON.parse(detail) } catch { return null }
    }
    return detail
  }

  const onWsReceived = async (evt) => {
    const msg = parseWsWhatsAppPayload(evt.detail)
    if (!msg || !active.value || !matchesEntity(msg)) return
    if (!whatsAppConnected.value) return
    upsertChatMessage(msg)
    await loadChatbotSession()
  }

  const onWsSent = async (evt) => {
    const msg = parseWsWhatsAppPayload(evt.detail)
    if (!msg || !active.value || !matchesEntity(msg)) return
    upsertChatMessage(msg)
    scrollToBottom()
    await loadChatbotSession()
  }

  const onWsConnected = () => refreshWhatsAppStatus()
  const onWsDisconnected = () => refreshWhatsAppStatus()

  const onPanelActivated = async () => {
    await refreshWhatsAppStatus()
    await loadChatbotSession()
    await loadChatHistory()
    startHealthPoll()
  }

  onMounted(async () => {
    if (typeof window === 'undefined') return
    window.addEventListener('whatsapp.connected', onWsConnected)
    window.addEventListener('whatsapp.disconnected', onWsDisconnected)
    window.addEventListener('whatsapp.message.received', onWsReceived)
    window.addEventListener('whatsapp.message.sent', onWsSent)
    if (active.value) {
      await onPanelActivated()
    }
  })

  onUnmounted(() => {
    if (typeof window === 'undefined') return
    window.removeEventListener('whatsapp.connected', onWsConnected)
    window.removeEventListener('whatsapp.disconnected', onWsDisconnected)
    window.removeEventListener('whatsapp.message.received', onWsReceived)
    window.removeEventListener('whatsapp.message.sent', onWsSent)
    stopHealthPoll()
    clearComposerRevealTimer()
    cancelRecording({ force: true })
  })

  watch(active, async (isActive, wasActive) => {
    if (isActive) {
      if (!wasActive) {
        await onPanelActivated()
      }
    } else if (wasActive) {
      stopHealthPoll()
      await markAllRead()
    }
  }, { immediate: true })

  watch(whatsAppConnected, (connected) => {
    if (active.value && connected) startHealthPoll()
    else stopHealthPoll()
  })

  watch(entityId, async (id) => {
    if (active.value && id) {
      await refreshWhatsAppStatus()
      await loadChatbotSession()
      await loadChatHistory()
    }
  })

  watch(showChatComposer, (visible) => {
    if (visible) nextTick(() => autoResizeWhatsAppMessage())
  })

  const hasContactPhone = computed(() => !!resolveRecipient())
  const hasWhatsAppContact = hasContactPhone

  return {
    chatMessages,
    newMessage,
    showEmojiPicker,
    messageInputEl,
    fileDoc,
    fileImg,
    autoScrollEnabled,
    chatContainer,
    previewWaImage,
    whatsAppConnected,
    whatsAppCanSend,
    whatsAppCanQueue,
    whatsAppOffline,
    composerStatusKey,
    chatbotSession,
    chatbotEnding,
    isRecording,
    showChatComposer,
    recordingDuration,
    hasContactPhone,
    hasWhatsAppContact,
    refreshWhatsAppStatus,
    loadChatHistory,
    markAllRead,
    sendMessage,
    sendTextMessage,
    sendDocumentFile,
    handleFilePick,
    triggerFilePick,
    toggleRecording,
    cancelRecording,
    stopRecording,
    toggleAutoScroll,
    toggleEmojiPicker,
    insertEmoji,
    autoResizeWhatsAppMessage,
    endChatbotAttendance,
    formatTime,
    formatVoiceDuration,
    renderWhatsAppMessage,
    getYoutubePreviews,
    messageTextWithoutYoutube,
    hasYoutubePreview,
    isMediaBubble,
    fileExtension,
    displayFileName,
    fileTypeLabel,
    fileIconClass,
    openWaImagePreview,
    closeWaImagePreview
  }
}
